import React, { useMemo, useState } from "react";
import { fileTree } from "../data/mock";

const defaultOpen = { projects: true, papers: true };

function matchesFilter(node, q) {
  if (!q) return true;
  if (node.name.toLowerCase().includes(q)) return true;
  if (node.children) return node.children.some((c) => matchesFilter(c, q));
  return false;
}

const FileRow = ({ node, depth, open, toggle, activeId, onOpen }) => {
  const isDir = node.kind === "dir";
  const expanded = Boolean(open[node.id]);
  const active = activeId === node.id;

  if (isDir) {
    return (
      <li>
        <button
          type="button"
          className={`ft-row${expanded ? " is-open" : ""}`}
          style={{ paddingLeft: `${0.4 + depth * 0.85}rem` }}
          aria-expanded={expanded}
          onClick={() => toggle(node.id)}
        >
          <span className={`ft-twist${expanded ? " is-open" : ""}`} aria-hidden="true" />
          <span className="ft-name">{node.name}</span>
        </button>
        {expanded && node.children ? (
          <ul>
            {node.children.map((child) => (
              <FileRow
                key={child.id}
                node={child}
                depth={depth + 1}
                open={open}
                toggle={toggle}
                activeId={activeId}
                onOpen={onOpen}
              />
            ))}
          </ul>
        ) : null}
      </li>
    );
  }

  if (node.kind === "pdf") {
    return (
      <li>
        <a
          className={`ft-row ft-file${active ? " is-active" : ""}`}
          style={{ paddingLeft: `${0.4 + depth * 0.85}rem` }}
          href={node.href}
          download
        >
          <span className="ft-twist-spacer" aria-hidden="true" />
          <span className="ft-name">{node.name}</span>
        </a>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        className={`ft-row ft-file${active ? " is-active" : ""}`}
        style={{ paddingLeft: `${0.4 + depth * 0.85}rem` }}
        onClick={() => onOpen(node)}
      >
        <span className="ft-twist-spacer" aria-hidden="true" />
        <span className="ft-name">{node.name}</span>
      </button>
    </li>
  );
};

const FileTree = ({ activeId, onOpen }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const nodes = useMemo(() => {
    if (!q) return fileTree;
    return fileTree
      .map((n) => {
        if (n.kind === "dir") {
          const kids = (n.children || []).filter((c) => matchesFilter(c, q));
          if (!kids.length && !n.name.toLowerCase().includes(q)) return null;
          return { ...n, children: kids };
        }
        return matchesFilter(n, q) ? n : null;
      })
      .filter(Boolean);
  }, [q]);

  const toggle = (id) => setOpen((s) => ({ ...s, [id]: !s[id] }));

  const filteredOpen = q
    ? Object.fromEntries(nodes.filter((n) => n.kind === "dir").map((n) => [n.id, true]))
    : open;

  return (
    <nav className="ft" aria-label="File browser">
      <p className="ft-title">File Browser</p>
      <p className="ft-cwd">lab/</p>
      <label className="ft-filter">
        <span className="visually-hidden">Filter files</span>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Filter files"
        />
      </label>
      <ul className="ft-tree">
        {nodes.map((node) => (
          <FileRow
            key={node.id}
            node={node}
            depth={0}
            open={filteredOpen}
            toggle={toggle}
            activeId={activeId}
            onOpen={onOpen}
          />
        ))}
      </ul>
      <p className="ft-hint">Double-click a cell to edit. Shift+Enter runs it.</p>
    </nav>
  );
};

export default FileTree;
